import React, { useContext, useEffect, useState } from "react"
import { graphql } from "gatsby"
import { Layout } from "../components/atoms/Layout"
import tw from "twin.macro"
import XseriesVideo from "../components/organisms/Xseries/Video"
import { Pixlee } from "../components/molecules/Pixlee"
import OtherXseriesModels from "../components/organisms/XseriesModel/OtherModels"
import XSeriesModelDetails from "../components/organisms/XseriesModel/XSeriesModelDetails"
import { SEO } from "../components/atoms/SEO"
import useTealiumView from "../hooks/Tealium/useTealiumView"
import { sanitizeSeries, vehicleCategory } from "../helpers"
import { DisclaimersContext } from "../contexts/Disclaimers"
import { setDisclaimers } from "../contexts/Disclaimers/actions"
import { LanguageContext } from "../contexts/Language"
import _ from "lodash"
import { Disclaimer } from "../contexts/Disclaimers/disclaimers.d"
import { useTealiumContext } from "../contexts/Tealium"
import { Model } from "../global"
import { OthersAlsoBought } from "../components/molecules/OthersAlsoBought"
import FeaturedSnippetModule from "../components/organisms/FeaturedSnippet/FeaturedSnippetModule"
import XSeriesFamilyDetails from "../components/organisms/Xseries/XSeriesFamilyDetails"

const XSeriesModelPage: React.FC<any> = ({ data, location }) => {
  const { page, familySelector } = data
  const [{ disclaimers }, dispatch] = useContext(DisclaimersContext)
  const { language } = useContext(LanguageContext)
  const [selectedModel, setSelectedModel] = useState<Model | null>(null) // it should start as null until you choose a model
  const [modelPosition, setModelPosition] = useState(0)

  // modelsData is an array of models that are on a single model page - sometimes the array length is 1, unless you have a model with multiple trims like RAV4 XP Trail and RAV4 XP Street which makes the array length 2
  const modelsData =
    page?.content[
      page?.content?.findIndex(
        (x: { _type: string }) => x._type == "xSeriesModelsHero",
      )
    ]?.customModels

  useEffect(() => {
    if (location?.state?.modelName && modelsData) {
      setSelectedModel(
        modelsData.find(
          (model: any) => model.models.name === location?.state?.modelName,
        )?.models,
      )
      setModelPosition(
        modelsData.findIndex(
          (model: any) => model.models.name === location?.state?.modelName,
        ),
      )
    } else if (modelsData) {
      setSelectedModel(modelsData[0].models)
      setModelPosition(0)
    }

    let modelDisclaimers = modelsData?.find(
      (model: any) => model.models.name === selectedModel,
    )?.models?.disclaimers?.highlightedFeatures

    if (modelDisclaimers) {
      modelDisclaimers?.forEach((disclaimer: any) => {
        disclaimer.disclaimer =
          language === "es"
            ? disclaimer.disclaimer_es
            : disclaimer.disclaimer_en
      })

      const containsAllModalDisclaimers = modelDisclaimers?.every(
        (disclaimer: any) =>
          disclaimers.some(
            (existingDisclaimer: Disclaimer) =>
              existingDisclaimer.code === disclaimer.code,
          ),
      )
      if (!containsAllModalDisclaimers) {
        const updatedDisclaimers = _.unionBy(
          modelDisclaimers,
          disclaimers,
          "code",
        )
        dispatch(setDisclaimers(updatedDisclaimers))
      }
    }

    const series = selectedModel?.series?.name
    const serieslc = sanitizeSeries(series)
    const modelName = selectedModel?.name
    const series_model = `${series} ${modelName}`
    const category = vehicleCategory(serieslc)

    handleTealView({
      page_type: "x-series",
      vehicle_model: series_model,
      tealium_event: "x-series_lander",
      page_name: `${category}_${series_model.toLocaleLowerCase()}`,
      vehicle_year: selectedModel?.year,
      vehicle_segment_vehicle_page: `${category}`,
      vehicle_model_truncated: series_model,
    })
    setVehicleTealData({
      vehicle_model: series_model,
      vehicle_year: selectedModel?.year,
      vehicle_segment_vehicle_page: `${category}`,
      vehicle_model_truncated: series_model,
      vehicle_model_trim: series_model,
      vehicle_series: series,
    })
  }, [])

  // Tealium
  const { handleTealView } = useTealiumView()
  const { setVehicleTealData } = useTealiumContext()

  return (
    <Layout>
      <SEO
        url={location.href}
        title={page.title}
        keywords={page.seo?.keywords}
        schema={page.seo?.schema}
        description={page.seo?.description}
        canonicalPath={page.seo?.canonicalPath}
      />
      <XSeriesFamilyDetails
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        {...familySelector?.nodes[0]}
      />
      <div css={[tw`bg-gradient-to-t from-gray-200 via-gray-200 to-gray-50`]}>
        <SanitySections
          data={page}
          modelPosition={modelPosition}
          model={selectedModel}
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
        />
      </div>
    </Layout>
  )
}

const SanitySections = ({
  data,
  modelPosition,
  model,
  selectedModel,
  setSelectedModel,
}: any): JSX.Element => {
  return (
    <>
      {data?.content?.map((section: any) => {
        switch (section?._type) {
          case "xSeriesModelsHero":
            return (
              <XSeriesModelDetails
                modelPosition={modelPosition}
                selectedModel={selectedModel}
                setSelectedModel={setSelectedModel}
                {...section}
              />
            )
          case "pixleeAlbum":
            return <Pixlee {...section} />
          case "videoSection":
            return <XseriesVideo {...section} />
          case "xSeriesOtherModels":
            return <OtherXseriesModels model={model} {...section} />
          case "othersAlsoBoughtSection":
            return <OthersAlsoBought {...section} />
          case "featuredSnippetModule":
            return <FeaturedSnippetModule {...section} />
        }
      })}
    </>
  )
}

export const query = graphql`
  query XSeriesModelPageQuery($id: String) {
    familySelector: allSanityXSeriesFamilySelector {
      nodes {
        xSeriesFamilyRef {
          model {
            id
            year
            jellybean {
              asset {
                _id
                altText
                gatsbyImageData(placeholder: NONE)
              }
            }
            name
            series {
              ...seriesPageModelSeries
            }
            colors {
              ...color
            }
            costs {
              baseMSRP
            }
          }
          name
        }
      }
    }
    page: sanityPageXseriesModel(id: { eq: $id }) {
      id
      title
      seo {
        keywords
        schema
        description
        canonicalPath
      }
      language
      disclaimers {
        code
        disclaimer
      }
      models {
        ... on SanityCustomModel {
          id
          year
          jellybean {
            asset {
              altText
              _id
              gatsbyImageData(placeholder: NONE)
            }
          }
          name
          series {
            ...seriesPageModelSeries
          }
          colors {
            ...color
          }
          costs {
            baseMSRP
          }
          accessoryCodes
        }
      }
      content {
        ...PixleeSectionData
        ...XSeriesVideoSection
        ...XSeriesOtherModels
        ...XSeriesModelHero
        ...OthersAlsoBoughtSectionData
        ...FeaturedSnippetModuleData
      }
    }
  }
`

export default XSeriesModelPage
